import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { PppChips } from "@/components/priorities/PppChips";
import { getPriorityStatusMeta } from "@/components/priorities/priorityCardTokens";
import { cn } from "@/lib/utils";
import { Crown, UserRound } from "lucide-react";
import type { PersonEntry } from "@/hooks/usePeopleBoard";

interface PersonDeliverableRowProps {
  entry: PersonEntry;
  index?: number;
  hero?: boolean;
}

export function PersonDeliverableRow({ entry, index, hero }: PersonDeliverableRowProps) {
  const navigate = useNavigate();
  const { priority, role, status } = entry;
  const meta = getPriorityStatusMeta(status);
  const StatusIcon = meta.icon;

  return (
    <button
      type="button"
      onClick={() => navigate(`/priorities/${priority.pid}`)}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-muted/50",
        hero && "border border-violet-200/70 bg-violet-50/40 dark:border-violet-800/50 dark:bg-violet-950/20",
      )}
    >
      {index !== undefined && (
        <span className="mt-0.5 w-4 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground">{index}</span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Badge
            variant="outline"
            className={cn(
              "h-4 shrink-0 gap-1 rounded px-1 text-[9px] font-semibold uppercase tracking-wide shadow-none",
              role === "DRI" ? "border-primary/25 bg-primary/10 text-primary" : "border-border/60 bg-muted/45 text-muted-foreground",
            )}
          >
            {role === "DRI" ? <Crown className="h-2.5 w-2.5" /> : <UserRound className="h-2.5 w-2.5" />}
            {role}
          </Badge>
          <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{priority.code}</span>
          <span className={cn("truncate text-foreground", hero ? "text-sm font-semibold" : "text-[13px] font-medium")}>{priority.name}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <PppChips ppps={priority.ppps} max={1} />
          <Badge variant="outline" className={cn("h-5 gap-1 rounded-full border px-2 text-[10px] font-semibold", meta.className)}>
            <StatusIcon className="h-2.5 w-2.5" />
            {meta.label}
          </Badge>
        </div>
      </div>
    </button>
  );
}
